import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { alpha, Box, Skeleton, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useAnnieRecommendations } from "@/hooks/useAnnieRecommendations";
import { useIsMobile } from "@/hooks/useMobile";
import { CONTROL_HEIGHT, FONT_SIZE, ICON_TILE, LETTER_SPACING, MOTION, NEUTRAL, SHADOW } from "@/theme/tokens";
import { MetaChip } from "@/ui/Badges/MetaChip";
import { AccentIconTile } from "@/ui/molecules/AccentIconTile";
import { SystemBanner } from "@/ui/molecules/SystemBanner";
import SystemButton from "@/ui/SystemButton/SystemButton";
import type { PickupApprovalItem } from "@/utils/models";

type Props = {
	departmentId: string;
	/** Gates the fetch — the panel only mounts this when open, so the query never runs in the background. */
	isOpen: boolean;
	/** Draft mode (or any state where a turn can't be sent) disables the row tap targets. */
	disabled: boolean;
	/** Whole-row tap → fills the composer AND auto-sends (the send() path, not prefill-only). */
	onApprovalSelect: (prompt: string) => void;
	/** The user's name for Annie. */
	assistantName: string;
};

/** How many rows the open screen shows — the rest live in the full approvals flow / can be asked for in chat. */
const MAX_ROWS = 3;

/** Row entrance: a small lift, staggered so the eye catches each arriving row. */
const ENTRANCE_LIFT_PX = 6;

/**
 * A dept-local `YYYY-MM-DD` rendered as a scanning label: `Today` / `Tomorrow` when it lands there, else
 * `ccc, MMM d` (matching the app's other compact date labels). Kept relative so the nearest, most-urgent
 * approvals read at a glance.
 */
function formatWhen(isoDate: string, times: string, t: (k: string) => string): string {
	const date = DateTime.fromISO(isoDate);
	if (!date.isValid) return times;
	const today = DateTime.now().startOf("day");
	// Round so a DST-straddling diff (e.g. 0.958d) still resolves to a whole-day Today/Tomorrow bucket.
	const days = Math.round(date.startOf("day").diff(today, "days").days);
	const dayLabel =
		days === 0 ? t("annie.opening.recommendations.today") : days === 1 ? t("annie.opening.recommendations.tomorrow") : date.toFormat("ccc, MMM d");
	return `${dayLabel} · ${times}`;
}

/** Coarse "how far out" badge label: This week / Next week / In N weeks / Next month / In N months. */
function relativeTimeframe(isoDate: string, t: TFunction): string {
	const date = DateTime.fromISO(isoDate);
	if (!date.isValid) return "";
	const now = DateTime.now();
	const weekDiff = Math.round(date.startOf("week").diff(now.startOf("week"), "weeks").weeks);
	if (weekDiff <= 0) return t("annie.opening.recommendations.timeframe.thisWeek");
	if (weekDiff === 1) return t("annie.opening.recommendations.timeframe.nextWeek");
	const monthDiff = Math.round(date.startOf("month").diff(now.startOf("month"), "months").months);
	if (monthDiff === 1) return t("annie.opening.recommendations.timeframe.nextMonth");
	if (monthDiff >= 2) return t("annie.opening.recommendations.timeframe.inMonths", { count: monthDiff });
	return t("annie.opening.recommendations.timeframe.inWeeks", { count: weekDiff });
}

/** Brand-tinted, tappable row overrides for the shared SystemButton — reads as a more prominent sibling of the capability chips below it. */
const rowSx = (theme: Theme, disabled: boolean) => ({
	// SystemButton sets a responsive width ({ sm: "fit-content" }) and minWidth "fit-content", so a plain
	// width:"100%" loses to its sm media-query rule and short rows hug their content. Override width per
	// breakpoint AND drop minWidth so every row is full-panel-width and long titles can wrap.
	width: { xs: "100%", sm: "100%" },
	minWidth: 0,
	maxWidth: "100%",
	whiteSpace: "normal",
	overflowWrap: "anywhere",
	overflow: "hidden",
	height: "auto",
	minHeight: 60,
	px: 1.25,
	py: 1,
	gap: 1.25,
	justifyContent: "flex-start",
	borderRadius: 1.5,
	border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
	backgroundColor: alpha(theme.palette.primary.main, 0.04),
	transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.HOVER}ms ease, background-color ${MOTION.DURATION.COLOR_SHIFT}ms ease, box-shadow ${MOTION.DURATION.COLOR_SHIFT}ms ease`,
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover": {
			backgroundColor: alpha(theme.palette.primary.main, 0.07),
			borderColor: alpha(theme.palette.primary.main, 0.34),
			boxShadow: SHADOW.PANEL_SOFT,
		},
		"&:hover .annie-rec-send": { transform: "translateX(2px)" },
	},
	"&:active": { transform: disabled ? "none" : "scale(0.99)" },
	"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
});

const metadataSx: SxProps<Theme> = {
	minWidth: 0,
	maxWidth: "100%",
	whiteSpace: "normal",
	overflowWrap: "anywhere",
	"& > span": { overflow: "visible", textOverflow: "clip" },
};

/**
 * Annie's open-screen "Approvals waiting" feed: the top pending approvals as tappable rows above the static
 * capability chips. Each row is keyed on shift identity (role · when), not a person — the queue is PHI-minimal
 * and can hold multiple applicants per shift. Tapping a row fills the composer with an approval prompt and
 * auto-sends it, so Annie immediately walks the head nurse through approving.
 *
 * Four states, error-before-empty so a failed fetch never masquerades as an "all clear": error → SystemBanner,
 * loading → skeleton rows, empty → a compact one-liner, items → rows. Data is prefetched at app startup (the
 * always-mounted panel warms the shared query), so opening the panel typically shows rows with no loading flash.
 */
export const AnnieRecommendationsFeed: FC<Props> = ({ departmentId, isOpen, disabled, onApprovalSelect, assistantName }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { isMobile } = useIsMobile();
	const reduceMotion = useReducedMotion();
	const { data, isLoading, isError, refetch } = useAnnieRecommendations(departmentId, isOpen);

	const [expanded, setExpanded] = useState(false);
	const rows = useMemo(() => (expanded ? (data ?? []) : (data ?? []).slice(0, MAX_ROWS)), [data, expanded]);
	const total = data?.length ?? 0;

	// View analytics: fire once per open session when the feed resolves (items or empty), not while loading
	// or errored. Resets when the panel closes so re-opening re-tracks.
	const viewedRef = useRef(false);
	useEffect(() => {
		if (!isOpen) {
			viewedRef.current = false;
			return;
		}
		if (isLoading || isError || data === undefined || viewedRef.current) return;
		viewedRef.current = true;
		track("annie_recommendations_viewed", { count: total });
	}, [isOpen, isLoading, isError, data, total, track]);

	const handleSelect = (item: PickupApprovalItem, position: number) => {
		if (disabled) return;
		const roleLabel = item.role ?? item.label;
		const when = formatWhen(item.date, item.times, t);
		track("annie_recommendation_selected", {
			role: item.role ?? "unknown",
			is_replacement: item.is_replacement,
			pending_applicants: item.pending_applicants,
			position,
		});
		onApprovalSelect(t("annie.opening.recommendations.prompt.approve", { role: roleLabel, when }));
	};

	// The eyebrow's count is only meaningful once the queue has resolved; while loading/errored it's just the label.
	const showCount = !isLoading && !isError && data !== undefined;

	// No pending approvals → render nothing and let the standard capability screen stand on its own (a richer
	// "other recommendations" surface for the empty case is a separate change). Error has its own banner below,
	// so an empty queue is never confused with a failed fetch.
	if (!isLoading && !isError && rows.length === 0) {
		return null;
	}

	return (
		<Box data-testid="annie-recommendations-feed" sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%" }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
				<Typography
					component="h3"
					sx={{
						minWidth: 0,
						overflowWrap: "anywhere",
						fontSize: FONT_SIZE.LABEL_XS,
						fontWeight: 700,
						letterSpacing: LETTER_SPACING.LABEL,
						textTransform: "uppercase",
						color: "text.secondary",
					}}
				>
					{t("annie.opening.recommendations.title")}
					{showCount && total > 0 && (
						<Box component="span" data-testid="annie-recommendations-count" sx={{ ml: 0.75, color: "text.primary", fontWeight: 800 }}>
							{total}
						</Box>
					)}
				</Typography>
				{showCount && total > 0 && (
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 0.5,
							minWidth: 0,
							overflowWrap: "anywhere",
							color: "primary.dark",
							fontSize: FONT_SIZE.LABEL_XS,
							fontWeight: 600,
						}}
					>
						<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
						<Box component="span" sx={{ minWidth: 0 }}>
							{t("annie.opening.recommendations.hint", { name: assistantName })}
						</Box>
					</Box>
				)}
			</Box>

			{isError ? (
				<SystemBanner
					dataTestId="annie-recommendations-error"
					tone="error"
					icon={<ErrorOutlineRoundedIcon />}
					title={t("annie.opening.recommendations.error")}
					description={t("annie.opening.recommendations.errorHint")}
					action={
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={() => refetch()}
							data-testid="annie-recommendations-retry"
							leadingIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
							sx={{
								color: "error.dark",
								fontWeight: 700,
								px: 1,
								minWidth: 0,
								maxWidth: "100%",
								minHeight: CONTROL_HEIGHT.COMPACT,
								height: "auto",
								whiteSpace: "normal",
								overflowWrap: "anywhere",
							}}
						>
							{t("annie.opening.recommendations.retry")}
						</SystemButton>
					}
				/>
			) : isLoading ? (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="annie-recommendations-loading">
					{Array.from({ length: MAX_ROWS }).map((_, index) => (
						<Box
							// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder list with no identity
							key={index}
							sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 1, minHeight: 60, borderRadius: 1.5, border: `1px solid ${NEUTRAL[200]}` }}
						>
							<Skeleton
								variant="rounded"
								width={ICON_TILE.SM.size}
								height={ICON_TILE.SM.size}
								sx={{ borderRadius: `${ICON_TILE.SM.size / 4}px`, flexShrink: 0 }}
							/>
							<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
								<Skeleton variant="text" width="55%" height={12} />
								<Skeleton variant="text" width="80%" height={10} />
							</Box>
						</Box>
					))}
				</Box>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="annie-recommendations-list">
					{rows.map((item, index) => {
						const roleLabel = item.role ?? item.label;
						const when = formatWhen(item.date, item.times, t);
						const timeframe = relativeTimeframe(item.date, t);
						return (
							<motion.div
								key={item.request_id}
								initial={reduceMotion ? false : { opacity: 0, transform: `translateY(${ENTRANCE_LIFT_PX}px)` }}
								animate={{ opacity: 1, transform: "translateY(0px)" }}
								transition={{
									duration: MOTION.DURATION.ENTRANCE_MD / 1000,
									ease: MOTION.EASE_OUT_POINTS,
									delay: reduceMotion ? 0 : (index * MOTION.STAGGER.STEP) / 1000,
								}}
								style={{ width: "100%" }}
							>
								<SystemButton
									buttonVariant="secondary"
									disabled={disabled}
									onClick={() => handleSelect(item, index)}
									data-testid={`annie-recommendation-row-${item.request_id}`}
									aria-label={t("annie.opening.recommendations.rowAria", { role: roleLabel, when })}
									sx={rowSx(theme, disabled)}
								>
									<AccentIconTile size="sm">{item.is_replacement ? <SwapHorizRoundedIcon /> : <PersonOutlineRoundedIcon />}</AccentIconTile>
									<Box sx={{ flex: 1, minWidth: 0, textAlign: "start", display: "flex", flexDirection: "column", gap: 0.375 }}>
										<Box
											sx={{
												display: "flex",
												flexDirection: isMobile ? "column" : "row",
												flexWrap: "wrap",
												alignItems: isMobile ? "stretch" : "center",
												columnGap: 0.75,
												rowGap: 0.375,
												minWidth: 0,
											}}
										>
											<Typography
												sx={{
													fontWeight: 700,
													fontSize: FONT_SIZE.EMPHASIS,
													color: "text.primary",
													lineHeight: 1.2,
													minWidth: 0,
													overflowWrap: "anywhere",
												}}
											>
												{roleLabel}
											</Typography>
											<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, minWidth: 0 }}>
												<MetaChip
													tone={item.is_replacement ? "warning" : "info"}
													label={
														item.is_replacement
															? t("annie.opening.recommendations.replacement")
															: t("annie.opening.recommendations.applied", { count: item.pending_applicants })
													}
													sx={metadataSx}
												/>
												{timeframe && <MetaChip tone="neutral" label={timeframe} sx={metadataSx} />}
											</Box>
										</Box>
										<Typography
											sx={{
												fontSize: FONT_SIZE.SECONDARY,
												color: "text.secondary",
												lineHeight: 1.25,
												minWidth: 0,
												overflowWrap: "anywhere",
											}}
										>
											{when}
										</Typography>
									</Box>
									<Box
										className="annie-rec-send"
										sx={{
											flexShrink: 0,
											display: "inline-flex",
											alignItems: "center",
											color: "primary.dark",
											transition: `transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
											"& > svg": { fontSize: 18 },
										}}
									>
										<SendRoundedIcon />
									</Box>
								</SystemButton>
							</motion.div>
						);
					})}
					{total > MAX_ROWS && (
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={() => setExpanded((value) => !value)}
							data-testid="annie-recommendations-show-more"
							sx={{
								alignSelf: "flex-start",
								px: 0.5,
								minWidth: 0,
								maxWidth: "100%",
								minHeight: CONTROL_HEIGHT.COMPACT,
								height: "auto",
								whiteSpace: "normal",
								overflowWrap: "anywhere",
								color: "primary.dark",
								fontWeight: 700,
							}}
						>
							{expanded ? t("annie.opening.recommendations.showLess") : t("annie.opening.recommendations.showMore", { count: total - MAX_ROWS })}
						</SystemButton>
					)}
				</Box>
			)}
		</Box>
	);
};
